import axios from 'axios'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { getTenantSlug } from '../utils/tenant'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true,
})

// Inject the tenant slug header on every request
api.interceptors.request.use(cfg => {
  cfg.headers['X-Tenant-Slug'] = getTenantSlug()
  return cfg
})
// ── refresh-token retry state ─────────────────────────────────────────────────
let isRefreshing = false
type PendingCall = { resolve: (v: unknown) => void; reject: (e: unknown) => void; cfg: object }
let pending: PendingCall[] = []

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error.config
    const status: number = error.response?.status

    const skipRefresh =
      cfg?._retry ||
      cfg?.url?.includes('/users/refresh-token') ||
      cfg?.url?.includes('/users/me')

    if (status === 401 && !skipRefresh) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pending.push({ resolve, reject, cfg })
        })
      }
      cfg._retry    = true
      isRefreshing  = true
      try {
        await api.post('/users/refresh-token')
        pending.forEach(p => p.resolve(api(p.cfg)))
        pending = []
        return api(cfg)
      } catch {
        pending.forEach(p => p.reject(new Error('Session expired')))
        pending = []
        useAuthStore.getState().logout()
        window.location.href = '/login'
        return Promise.reject(error)
      } finally {
        isRefreshing = false
      }
    }

    // Non-auth errors → show toast (unless caller opted out)
    if (status && status !== 401 && !cfg?._skipToast) {
      const msg: string =
        error.response?.data?.detail ??
        error.response?.data?.title  ??
        error.message                ??
        'An unexpected error occurred'
      useToastStore.getState().add({ type: 'error', message: msg })
    }

    return Promise.reject(error)
  }
)

export const http = {
  get:  async <T,>(url: string, params?: object, skipToast?: boolean) => (await api.get<T>(url, { params, ...(skipToast && { _skipToast: true }) })).data,
  post: async <T,>(url: string, body?: unknown)  => (await api.post<T>(url, body)).data,
  put:  async <T,>(url: string, body?: unknown)  => (await api.put<T>(url, body)).data,
  del:  async <T,>(url: string)                  => (await api.delete<T>(url)).data,
}

export default api